/**
 * Premium Promo Banner System
 * CRO-Optimized Seasonal Campaign Banners
 *
 * Custom Elements for Shopify Theme Integration
 */

// Prevent duplicate execution when script is loaded multiple times
if (typeof window.PromoBannerLoaded === 'undefined') {
window.PromoBannerLoaded = true;

/* ========================================
   BASE PROMO BANNER CLASS
   ======================================== */

class PromoBanner extends HTMLElement {
  constructor() {
    super();

    // Configuration from data attributes
    this.promoId = this.dataset.promoId || this.generateId();
    this.startDate = this.dataset.startDate ? new Date(this.dataset.startDate) : null;
    this.endDate = this.dataset.endDate ? new Date(this.dataset.endDate) : null;
    this.dismissible = this.dataset.dismissible === 'true';
    this.cookieExpire = parseInt(this.dataset.cookieExpire) || 1;
    this.delay = parseInt(this.dataset.delay) || 0;

    // State
    this.isShown = false;
    this.countdownInterval = null;
  }

  connectedCallback() {
    if (this.shouldShow()) {
      if (this.delay > 0) {
        setTimeout(() => this.show(), this.delay);
      } else {
        // Small delay to ensure CSS is loaded
        requestAnimationFrame(() => {
          this.show();
        });
      }
      this.bindEvents();
      this.initCountdown();
    } else {
      this.remove();
    }
  }

  disconnectedCallback() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  generateId() {
    return 'promo-' + Math.random().toString(36).substr(2, 9);
  }

  shouldShow() {
    const now = new Date();

    // Check date range
    if (this.startDate && now < this.startDate) return false;
    if (this.endDate && now > this.endDate) return false;

    // Check if dismissed
    if (this.dismissible && this.getCookie(`promo-${this.promoId}`) === 'closed') {
      return false;
    }

    return true;
  }

  /* Cookie Management */
  setCookie(cname, cvalue, exdays) {
    const d = new Date();
    d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
    const expires = 'expires=' + d.toUTCString();
    document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
  }

  getCookie(cname) {
    const name = cname + '=';
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  deleteCookie(name) {
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/';
  }

  /* Display Methods */
  show() {
    this.style.display = '';
    requestAnimationFrame(() => {
      this.classList.add('is-visible');
      this.isShown = true;
    });
  }

  hide() {
    this.classList.remove('is-visible');
    this.isShown = false;

    // Wait for transition to complete
    const transitionDuration = parseFloat(
      getComputedStyle(this).getPropertyValue('--promo-transition') || '0.3s'
    ) * 1000;

    setTimeout(() => {
      this.style.display = 'none';
    }, transitionDuration);
  }

  dismiss() {
    if (this.dismissible) {
      this.setCookie(`promo-${this.promoId}`, 'closed', this.cookieExpire);
    }

    this.classList.remove('is-visible');

    setTimeout(() => {
      this.remove();
    }, 300);
  }

  /* Event Binding */
  bindEvents() {
    const closeBtn = this.querySelector('[data-promo-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.dismiss();
      });
    }

    // Keyboard accessibility - ESC to close
    if (this.dismissible) {
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.dismiss();
        }
      });
    }
  }

  /* Countdown Timer */
  initCountdown() {
    const countdownEl = this.querySelector('[data-promo-countdown]');
    if (!countdownEl) return;

    const targetDate = countdownEl.dataset.promoCountdown;
    if (!targetDate) return;

    const countDownDate = new Date(targetDate).getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = countDownDate - now;

      if (distance < 0) {
        // Countdown finished
        clearInterval(this.countdownInterval);
        countdownEl.classList.add('expired');

        // Optionally hide the banner when countdown expires
        if (this.dataset.hideOnExpire === 'true') {
          this.dismiss();
        }
        return;
      }

      // Calculate time units
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      // Update DOM
      const daysEl = countdownEl.querySelector('[data-days]');
      const hoursEl = countdownEl.querySelector('[data-hours]');
      const minutesEl = countdownEl.querySelector('[data-minutes]');
      const secondsEl = countdownEl.querySelector('[data-seconds]');

      if (daysEl) daysEl.textContent = days.toString().padStart(2, '0');
      if (hoursEl) hoursEl.textContent = hours.toString().padStart(2, '0');
      if (minutesEl) minutesEl.textContent = minutes.toString().padStart(2, '0');
      if (secondsEl) secondsEl.textContent = seconds.toString().padStart(2, '0');
    };

    // Initial update
    updateCountdown();

    // Update every second
    this.countdownInterval = setInterval(updateCountdown, 1000);
  }
}

/* ========================================
   HERO CAMPAIGN BANNER
   ======================================== */

class PromoBannerHero extends PromoBanner {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    // Add glow effect if enabled
    if (this.dataset.glow === 'true') {
      this.classList.add('has-glow');
    }
  }
}

/* ========================================
   INLINE PROMO STRIP
   ======================================== */

class PromoBannerStrip extends PromoBanner {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize marquee if enabled
    if (this.dataset.marquee === 'true') {
      this.initMarquee();
    }
  }

  initMarquee() {
    const marqueeContent = this.querySelector('.promo-banner__marquee');
    if (!marqueeContent) return;

    // Clone content for seamless loop
    const clone = marqueeContent.cloneNode(true);
    marqueeContent.parentNode.appendChild(clone);

    this.classList.add('has-marquee');
  }
}

/* ========================================
   CART URGENCY BANNER
   ======================================== */

class PromoBannerCartUrgency extends PromoBanner {
  constructor() {
    super();
    this.freeShippingThreshold = parseFloat(this.dataset.freeShippingThreshold) || 0;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.freeShippingThreshold > 0) {
      this.updateShippingProgress();
    }
  }

  updateShippingProgress() {
    const progressFill = this.querySelector('.promo-banner__progress-fill');
    const progressText = this.querySelector('.promo-banner__progress-text');

    if (!progressFill) return;

    // Get current cart total from Shopify
    const cartTotal = window.Shopify?.currency?.active
      ? this.getCartTotal()
      : 0;

    const percentage = Math.min((cartTotal / this.freeShippingThreshold) * 100, 100);
    const remaining = Math.max(this.freeShippingThreshold - cartTotal, 0);

    progressFill.style.width = percentage + '%';

    if (progressText) {
      if (remaining > 0) {
        progressText.innerHTML = `Add <strong>${this.formatMoney(remaining)}</strong> more for free shipping!`;
      } else {
        progressText.innerHTML = `<strong>You've unlocked free shipping!</strong>`;
      }
    }
  }

  getCartTotal() {
    // This would typically come from Shopify's cart object
    // For now, we'll look for a data attribute
    return parseFloat(this.dataset.cartTotal) || 0;
  }

  formatMoney(cents) {
    // Simple money formatting - can be enhanced with Shopify's money filters
    return '$' + (cents / 100).toFixed(2);
  }
}

/* ========================================
   PRODUCT PAGE BADGE
   ======================================== */

class PromoBannerProductBadge extends PromoBanner {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }
}

/* ========================================
   FLOATING STICKY BAR
   ======================================== */

class PromoBannerSticky extends PromoBanner {
  constructor() {
    super();
    this.scrollThreshold = parseInt(this.dataset.scrollThreshold) || 200;
    this.boundScrollHandler = this.handleScroll.bind(this);
  }

  connectedCallback() {
    // Don't call super - we have custom show logic
    if (!this.shouldShow()) {
      this.remove();
      return;
    }

    this.bindEvents();
    this.initCountdown();

    // Show based on scroll position
    if (this.dataset.showOnScroll === 'true') {
      window.addEventListener('scroll', this.boundScrollHandler, { passive: true });
      this.handleScroll(); // Check initial position
    } else {
      // Show after delay
      const delay = parseInt(this.dataset.delay) || 2000;
      setTimeout(() => this.show(), delay);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.boundScrollHandler);
  }

  handleScroll() {
    const scrollY = window.scrollY || window.pageYOffset;

    if (scrollY > this.scrollThreshold && !this.isShown) {
      this.show();
    } else if (scrollY <= this.scrollThreshold && this.isShown && this.dataset.hideOnScrollUp === 'true') {
      this.hide();
    }
  }

  show() {
    this.style.display = '';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('is-visible');
        this.isShown = true;
      });
    });
  }
}

/* ========================================
   EXIT-INTENT MODAL
   ======================================== */

class PromoBannerExitModal extends PromoBanner {
  constructor() {
    super();
    this.hasTriggered = false;
    this.boundExitHandler = this.handleExitIntent.bind(this);
    this.boundKeyHandler = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    // Don't call super - we have custom show logic
    if (!this.shouldShow()) {
      this.remove();
      return;
    }

    this.bindEvents();
    this.initCountdown();

    // Exit intent detection
    if (this.dataset.exitIntent === 'true') {
      document.addEventListener('mouseout', this.boundExitHandler);
    }

    // Time-based trigger
    const timeDelay = parseInt(this.dataset.timeDelay) || 0;
    if (timeDelay > 0) {
      setTimeout(() => {
        if (!this.hasTriggered) {
          this.showModal();
        }
      }, timeDelay);
    }

    // Scroll percentage trigger
    const scrollPercent = parseInt(this.dataset.scrollPercent) || 0;
    if (scrollPercent > 0) {
      this.initScrollTrigger(scrollPercent);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mouseout', this.boundExitHandler);
    document.removeEventListener('keydown', this.boundKeyHandler);
  }

  handleExitIntent(e) {
    // Only trigger when mouse leaves through top of viewport
    if (e.clientY < 10 && !this.hasTriggered) {
      this.showModal();
    }
  }

  initScrollTrigger(targetPercent) {
    const handler = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (window.scrollY / scrollHeight) * 100;

      if (scrollPercent >= targetPercent && !this.hasTriggered) {
        this.showModal();
        window.removeEventListener('scroll', handler);
      }
    };

    window.addEventListener('scroll', handler, { passive: true });
  }

  showModal() {
    this.hasTriggered = true;
    this.show();

    // Trap focus
    document.addEventListener('keydown', this.boundKeyHandler);

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Focus first focusable element
    const focusable = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable) {
      setTimeout(() => focusable.focus(), 100);
    }
  }

  handleKeydown(e) {
    if (e.key === 'Escape') {
      this.dismiss();
    }

    // Trap focus within modal
    if (e.key === 'Tab') {
      const focusableElements = this.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      } else if (!e.shiftKey && document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  bindEvents() {
    super.bindEvents();

    // Close on backdrop click
    this.addEventListener('click', (e) => {
      if (e.target === this) {
        this.dismiss();
      }
    });

    // Dismiss link (e.g., "No thanks")
    const dismissLink = this.querySelector('[data-promo-dismiss]');
    if (dismissLink) {
      dismissLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.dismiss();
      });
    }
  }

  dismiss() {
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.boundKeyHandler);
    super.dismiss();
  }
}

/* ========================================
   REGISTER CUSTOM ELEMENTS
   ======================================== */

customElements.define('promo-banner-hero', PromoBannerHero);
customElements.define('promo-banner-strip', PromoBannerStrip);
customElements.define('promo-banner-cart-urgency', PromoBannerCartUrgency);
customElements.define('promo-banner-product-badge', PromoBannerProductBadge);
customElements.define('promo-banner-sticky', PromoBannerSticky);
customElements.define('promo-banner-exit-modal', PromoBannerExitModal);

/* ========================================
   UTILITY: GLOBAL COUNTDOWN MANAGER
   ======================================== */

window.PromoBannerCountdown = {
  init: function(selector) {
    const countdowns = document.querySelectorAll(selector || '[data-promo-countdown]');
    countdowns.forEach(el => {
      if (!el.closest('.promo-banner')) {
        // Standalone countdown - initialize manually
        this.startCountdown(el);
      }
    });
  },

  startCountdown: function(el) {
    const targetDate = el.dataset.promoCountdown;
    if (!targetDate) return;

    const countDownDate = new Date(targetDate).getTime();

    const interval = setInterval(() => {
      const now = new Date().getTime();
      const distance = countDownDate - now;

      if (distance < 0) {
        clearInterval(interval);
        el.classList.add('expired');
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      const daysEl = el.querySelector('[data-days]');
      const hoursEl = el.querySelector('[data-hours]');
      const minutesEl = el.querySelector('[data-minutes]');
      const secondsEl = el.querySelector('[data-seconds]');

      if (daysEl) daysEl.textContent = days.toString().padStart(2, '0');
      if (hoursEl) hoursEl.textContent = hours.toString().padStart(2, '0');
      if (minutesEl) minutesEl.textContent = minutes.toString().padStart(2, '0');
      if (secondsEl) secondsEl.textContent = seconds.toString().padStart(2, '0');
    }, 1000);

    return interval;
  }
};

} // End of PromoBannerLoaded check
